import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { FileCounts, FolderData, PilotData } from "src/app/models/models";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";

@Component({
	selector: "app-folders",
	templateUrl: "./folders.component.html",
	styleUrl: "./folders.component.scss",
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PilotDataFoldersComponent implements OnInit, OnDestroy {
	selectedPilot?: PilotData;
	staffNumber?: string;
	name?: string;
	fileCounts: FileCounts[] = [];
	folders: FolderData[] = [];

	folderSub!: Subscription;
	routeSub!: Subscription;

	sessionMasterData = this.sharedService.sessionMasterData;

	constructor(
		public sharedService: SharedService,
		public router: Router,
		private httpService: HttpService,
		private route: ActivatedRoute,
		private loaderService: LoaderService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit() {
		this.sharedService.setActivePage(RouteType.PILOT_DATA);
		this.routeSub = this.route.queryParams.subscribe((params) => {
			this.staffNumber = params["staffNumber"];
			this.name = params["name"];
			if (this.staffNumber) {
				this.initFolderCategory(this.staffNumber);
			}
		});
	}

	initFolderCategory(staffNumber: string) {
		this.loaderService.setLoading(true);

		this.folderSub = this.httpService.getFileCounts(staffNumber).subscribe({
			next: (response) => {
				this.fileCounts = response.data.fileCounts;
				this.mapFileCountsToFolders();
				this.loaderService.setLoading(false);
				this.cdr.detectChanges();
			},
			error: (error) => {
				console.error("Error loading file counts:", error);
				this.loaderService.setLoading(false);
			},
		});
	}

	mapFileCountsToFolders() {
		this.folders = this.folderCategories.map((category, index) => ({
			id: index + 1,
			name: category.value ?? "",
			code: category.code,
			count: this.fileCounts.find((fc) => fc.code === category.code)?.count || 0,
			lastUploaded: this.fileCounts.find((fc) => fc.code === category.code)?.lastUploaded,
		}));
	}

	folderIsEmpty(folder: FolderData): boolean {
		return folder.count === 0;
	}

	get folderCategories() {
		return this.sessionMasterData?.documentTypes || [];
	}

	viewFiles(folder: FolderData) {
		const { id: folderId, count } = folder;

		if (count === 0) {
			this.router.navigate([RouteType.PILOT_DATA_FILES], {
				queryParams: {
					staffNumber: this.staffNumber,
					name: this.name,
					category: folder.code,
					categoryName: folder.name,
					folderId: folder.id,
				},
			});
			return;
		}

		this.router.navigate(["pilot-data", this.staffNumber, "folders", folderId, "files"], {
			queryParams: {
				staffNumber: this.staffNumber,
				name: this.name,
				category: folder.code,
				categoryName: folder.name,
				folderId: folder.id,
			},
		});
	}

	uploadData(staffNumber: string) {
		this.router.navigate([RouteType.PILOT_DATA_UPLOAD], {
			queryParams: {
				staffNumber: staffNumber,
				name: this.name,
			},
		});
	}

	goBack() {
		this.router.navigate([RouteType.PILOT_DATA]);
	}

	ngOnDestroy() {
		if (this.routeSub) {
			this.routeSub?.unsubscribe();
		}
		if (this.folderSub) {
			this.folderSub?.unsubscribe();
		}
	}
}
